package cz.lhoracek.mapl.ui.screens.session

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import cz.lhoracek.mapl.data.model.Round
import cz.lhoracek.mapl.data.model.SessionWithPlayers
import cz.lhoracek.mapl.data.repository.SessionRepository
import cz.lhoracek.mapl.domain.normalizeBetToPowerOfTwo
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class SessionViewModel(
    private val repository: SessionRepository,
    private val sessionId: String,
) : ViewModel() {

    val session: StateFlow<SessionWithPlayers?> =
        repository.observeSessionWithPlayers(sessionId)
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    // Round hidden from UI after swipe – not yet deleted from Firestore
    private val _pendingDeleteRound = MutableStateFlow<Round?>(null)
    val pendingDeleteRound: StateFlow<Round?> = _pendingDeleteRound.asStateFlow()

    // ID of the round most recently restored by undo; consumed by the item composable
    private val _lastUndoneRoundId = MutableStateFlow<String?>(null)
    val lastUndoneRoundId: StateFlow<String?> = _lastUndoneRoundId.asStateFlow()

    fun updateBet(newBet: Int) {
        viewModelScope.launch { repository.updateBet(sessionId, normalizeBetToPowerOfTwo(newBet)) }
    }

    fun endSession() {
        viewModelScope.launch { repository.endSession(sessionId) }
    }

    fun reactivateSession() {
        viewModelScope.launch { repository.reactivateSession(sessionId) }
    }

    /** Hides the round in UI immediately; Firestore not touched yet. */
    fun markForDelete(round: Round) {
        _pendingDeleteRound.value = round
    }

    /** Snackbar timed out – now actually delete from Firestore. */
    fun commitDelete() {
        val round = _pendingDeleteRound.value ?: return
        _pendingDeleteRound.value = null
        viewModelScope.launch { repository.deleteRound(sessionId, round) }
    }

    /** User tapped Zpět – restore item in UI, no Firestore call needed. */
    fun cancelDelete() {
        _lastUndoneRoundId.value = _pendingDeleteRound.value?.id
        _pendingDeleteRound.value = null
    }

    /** Called by the item composable after it has consumed the undo signal. */
    fun clearUndoSignal() {
        _lastUndoneRoundId.value = null
    }
}
